import { Gender, Initial, kycStatusType, LicenceTagType, PaginationRequest } from "src/types/commonTypes";
import client from "../client";
import publicClient from "../publicClient";
import internalClient from "../internalClient";
import { EMPTY_STRING } from "src/components/constant/constant";
import { buildGraphqlFieldsFromMap, graphqlSearchUserFields } from "src/utils/graphqlFieldMaps";
import { normalizePhoneForApi } from "src/utils/phoneUtils";

export type CreateUserRequest = {
  firstName: string;
  lastName: string;
  gender: Gender;
  initial: Initial;
  email: string;
  phone: string;
  role: string;
  dob: string;
  city: string;
  country: string;
  line1: string;
  line2: string;
  postcode: string;
  state: string;
  organisationId?: string;
  userName?: string;
};

export type SaveUserPreferencesInput = {
  user_id: string;
  ndis_number: string;
  licence_tag?: LicenceTagType;
  exclude_license: boolean;
  // Per-user coding defaults (#3) — omit to preserve existing (backend merges).
  default_gl_code?: string;
  default_tracking_code?: string;
  default_gst_treatment?: "INCLUDED" | "NONE";
  // Per-user approver/verifier override (#4) — omit to preserve existing.
  approvers?: string[];
  verifiers?: string[];
  // Email notification toggles — omit to preserve existing (backend merges).
  notification_preferences?: NotificationPreferencesInput;
};

export type NotificationPreferencesInput = {
  new_expenses_daily?: boolean;
  receipt_reminder?: boolean;
  ready_to_approve?: boolean;
  ready_to_verify?: boolean;
  monthly_draft_digest?: boolean;
};

export const saveUserPreferences = (data: SaveUserPreferencesInput) => {
  return client({
    query: `
      mutation SaveUserPreferences ($input: UserPreferencesInput) {
        save_user_preferences(input: $input) {
          user_id
        }
      }
    `,
    variables: {
      input: {
        user_id: data.user_id,
        ndis_number: data.ndis_number,
        licence_tag: data.licence_tag ?? "PROVIDER_PAID",
        // device_token intentionally omitted: the backend preserves the stored
        // tokens (sending [] here used to wipe mobile push tokens).
        plan_manage_id: EMPTY_STRING,
        exclude_license: data.exclude_license,
        // Pass-through: undefined keys are omitted from the request, so the
        // backend preserves the stored value (?? merge) — lets separate profile
        // sections save coding-defaults and approvers independently.
        default_gl_code: data.default_gl_code,
        default_tracking_code: data.default_tracking_code,
        default_gst_treatment: data.default_gst_treatment,
        approvers: data.approvers,
        verifiers: data.verifiers,
        notification_preferences: data.notification_preferences
      }
    }
  });
};
export const createUser = (data: CreateUserRequest) => {
  return client({
    query: `
      mutation createUser ($input: CreateUserInput) {
        create_user(input: $input) {
          user_id
        }
      }
    `,
    variables: {
      input: {
        address: {
          city: data.city,
          country: data.country,
          line_1: data.line1,
          line_2: data.line2,
          post_code: data.postcode,
          state: data.state
        },
        date_of_birth: data.dob,
        email: data.email.toLowerCase(),
        first_name: data.firstName,
        gender: data.gender,
        initial: data.initial,
        last_name: data.lastName,
        phone: normalizePhoneForApi(data.phone),
        username: data.userName,
        organisation_id: data.organisationId,
        role_name: data.role
      }
    }
  });
};

export type SearchUserRequest = {
  user_filter?: {
    is_active?: boolean | null;
    email?: string | null;
    full_name?: string | null;
    organisation_id?: number | string | null;
    organisationDisplayId?: string;
    role?: string;
    roles?: string[];
    user_ids?: string[];
    owns_wallet?: boolean;
  };
  searchCriteria?: string;
  size?: number | null;
  from?: number | null;
};

export const searchUser = (data: SearchUserRequest, fieldsObj: Record<string, unknown> = graphqlSearchUserFields) => {
  const fields = buildGraphqlFieldsFromMap(fieldsObj);

  return client({
    query: `query SearchUser($input: SearchInput) {
      search_user(input: $input){
        ${fields}
      }
    }`,
    variables: {
      input: {
        filter: {
          user_filter: data.user_filter
        },
        search_criteria: data.searchCriteria,
        size: data.size,
        from: data.from
      }
    }
  });
};

export const searchOrganisationUsers = (data: SearchUserRequest) => {
  return client({
    query: `query searchOrganisationUsers($input: SearchInput) {
      search_organisation_users(input: $input){
        total_pages
        total_size
        user_listing {
          attributes {
            address {
            city
            country
            post_code
            line_1
            line_2
            state
            }
            first_name
            last_name
          }
          current_organisation_role
          created_at
          display_id
          email
          is_active
          phone
          user_id
          username
          roles {
            value
            organisation_id
          }
        }
      }
    }`,
    variables: {
      input: {
        filter: {
          user_filter: data.user_filter
        },
        search_criteria: data.searchCriteria,
        size: data.size,
        from: data.from
      }
    }
  });
};

export const searchInternalUser = (data: SearchUserRequest) => {
  return internalClient({
    query: `query SearchUser($input: SearchInput) {
      internal_search_user(input: $input){
        total_pages
        total_size
        user_listing {
          attributes {
            first_name
            last_name
          }
          current_organisation_role
          created_at
          display_id
          email
          is_active
          phone
          user_id
          username
          roles {
            value
            organisation_id
          }
        }
      }
    }`,
    variables: {
      input: {
        filter: {
          user_filter: data.user_filter
        },
        search_criteria: data.searchCriteria,
        size: data.size,
        from: data.from
      }
    }
  });
};

export const userOrganisation = (userId: string) => {
  return internalClient({
    query: `query UserOrganisation($input: UserOrganisationsInput) {
      get_user_organisations(input: $input){
        organisation_id
        name
        display_id
        role
      }
    }`,
    variables: {
      input: {
        user_id: userId
      }
    }
  });
};

// Lightweight get_user selecting only the per-user approver/verifier overrides —
// used to populate the Users-table Approver column without the full user payload.
export const getUserApprovers = (userId: string) => {
  return client(
    {
      query: `
    query GetUserApprovers($input: GetUserInput) {
      get_user(input: $input) {
        user_id
        preferences {
          approvers
          verifiers
        }
      }
    }`,
      variables: { input: { user_id: userId } }
    },
    // Background lookup: a user that no longer exists (stale search index) must
    // not raise a global error toast, and an authorizer deny (a Team Member
    // approver reading the cardholder) must not show the access panel or bounce
    // the session — the caller falls back to the org defaults.
    { suppressErrorToast: true, suppressAuthError: true }
  );
};

export type getUserById = {
  user_id?: string;
  display_id?: string;
};

export const getUserById = (data: getUserById) => {
  return client({
    query: `
    query GetUserById($input: GetUserInput) {
      get_user(input: $input) {
        user_id
        username
        cognito_username
        email
        guardian_id
        is_active
        display_id
        created_at
        preferences {
          ndis_number
          licence_tag
          exclude_license
          default_gl_code
          default_tracking_code
          default_gst_treatment
          approvers
          verifiers
          notification_preferences {
            new_expenses_daily
            receipt_reminder
            ready_to_approve
            ready_to_verify
            monthly_draft_digest
          }
        }
        attributes {
          address {
            city
            country
            post_code
            line_1
            line_2
            state
          }
          first_name
          last_name
          gender
          initial
          date_of_birth
          profile_image
        }
        security_status
        phone
        roles {
          value
          organisation_id
        }
      }    
    }`,
    variables: {
      input: data
    }
  });
};

export const getUserName = (userId: string) => {
  return client(
    {
      query: `query GetUserName($input: GetUserInput) {
      get_user_name(input: $input)
    }`,
      variables: {
        input: {
          user_id: userId
        }
      }
    },
    { suppressErrorToast: true }
  );
};

export type UpdateUserRequest = {
  id?: number | string;
  organisationId?: string;
  active?: boolean;
  email?: string;
  userName?: string;
  initial?: Initial | null;
  firstName?: string;
  lastName?: string;
  dob?: string;
  phoneNumber?: string;
  gender?: Gender | null;
  role?: string;
  state?: string;
  city?: string;
  line1?: string;
  line2?: string;
  zipcode?: string;
  country?: string;
  accessCode?: string;
  fullName?: string;
  guardianId?: number | string;
  kycStatus?: kycStatusType | null;
  profileImage?: string;
};

export const updateUser = (data: UpdateUserRequest) => {
  return client({
    query: `mutation UpdateUser($input: UpdateUserInput) {
        update_user(input: $input) {
            user_id
        }
    }`,
    variables: {
      input: {
        organisation_id: data.organisationId,
        user_id: data.id,
        email: data.email,
        first_name: data.firstName,
        last_name: data.lastName,
        username: data.userName,
        gender: data.gender,
        phone: normalizePhoneForApi(data.phoneNumber),
        // // profile_image: data.profileImage,
        date_of_birth: data.dob,
        address: {
          line_1: data.line1,
          line_2: data.line2,
          city: data.city,
          state: data.state,
          post_code: data.zipcode,
          country: data.country
        }
        // guardian_id: data.guardianId
      }
    }
  });
};

export const getUserWallets = (userId: string) => {
  return client({
    query: `
      query GetUserWallets($input: UserQueryInput) {
        get_user_wallets(input: $input) {
          balance
          display_id 
          description 
          name 
          wallet_id 
          wallet_type
          user {
            email
            user_id
            attributes {
              last_name
              first_name
            }
          }
          group {
            group_id
            name
            group_type
          }
        }
      }
    `,
    variables: {
      input: {
        user_id: userId
      }
    }
  });
};

export type ConnectUserRequest = {
  organisationId: string;
  roleName: string;
  userId: string;
};

export const connectUser = (data: ConnectUserRequest) => {
  return client({
    query: `
    mutation ConnectUserToOrganisation($input: ConnectUserToOrganisationInput) {
      connect_user_to_organisation(input: $input) {
        card_id
        created_at
        organisation_id
        relationship_id
        status
        sub_type
        type
        user_id
        value
        wallet_id
      }
    }`,
    variables: {
      input: {
        organisation_id: data.organisationId,
        role_name: data.roleName,
        user_id: data.userId
      }
    }
  });
};

export type UpdateUserRoleRequest = {
  organisationId: string;
  roleName: string;
  userId: string;
};

export const updateUserRole = (data: UpdateUserRoleRequest) => {
  return client({
    query: `
    mutation UpdateUserRole($input: UpdateUserRoleInput) {
      update_user_role(input: $input) {
        organisation_id
        relationship_id
        user_id
        value
        type
        sub_type
        status
      }
    }`,
    variables: {
      input: {
        organisation_id: data.organisationId,
        role_name: data.roleName,
        user_id: data.userId
      }
    }
  });
};

export type SuspendUserRequest = {
  organisationId: string;
  userId: string;
};

export const suspendUser = (data: SuspendUserRequest) => {
  return client({
    query: `
    mutation SuspendUser($input: SuspendUserInput) {
      suspend_user(input: $input) {
        card_id
        created_at
        relationship_id
        organisation_id
        status
        sub_type
        type
        user_id
        value
        wallet_id
      }
    }`,
    variables: {
      input: {
        organisation_id: data.organisationId,
        user_id: data.userId
      }
    }
  });
};

// CPC: fire the one-time "get the mobile app" email (Team member first sign-in).
export const notifyAppDownload = (data: { email: string; firstName?: string }) => {
  return client({
    query: `
    mutation NotifyAppDownload($input: NotifyAppDownloadInput) {
      notify_app_download(input: $input) {
        sent
      }
    }`,
    variables: {
      input: {
        email: data.email,
        first_name: data.firstName ?? ""
      }
    }
  });
};

export const getUserByEmail = (email: string) => {
  return client({
    query: `
    query GetUser($input: GetUserInput) {
      get_user(input: $input) {
        display_id
        email
        is_active
        security_status
        user_id
        username
        phone
        roles {
          card_id
          created_at
          organisation_id
          relationship_id
          sub_type
          status
          type
          user_id
          value
          wallet_id
        }
        attributes {
          address {
            city
            country
            post_code
            line_1
            line_2
            state
          }
          first_name
          last_name
          gender
          initial
          date_of_birth
        }
      }
    }`,
    variables: {
      input: {
        email: email
      }
    }
  });
};

export const getUserCard = (userId: string, organisationId: string) => {
  return client({
    query: `query GetUserCards($input: GetUserCardsInput) { 
      get_user_cards(input: $input) { 
        cards {
          ppan
          card_type
          card_status
          card_id
          holder_name
          user {
            email
            attributes {
              first_name
              last_name
            }
          }
          card_setting {
            card_id
            ppan
            wallet_id
            card_setting_status
            card_setting_id
            expires_at
          }
        }
      } 
    }`,
    variables: {
      input: {
        user_id: userId,
        organisation_id: organisationId
      }
    }
  });
};

export const checkUserNameAvailability = (userName: string) => {
  return publicClient({
    query: `
      query IsUserNameAvailable($username: String!) {
        is_username_available(username: $username)
      } 
    `,
    variables: {
      username: userName
    }
  });
};

export const checkEmailAvailability = (email: string) => {
  return publicClient({
    query: `
      query IsEmailAvailable($email: String!) {
        is_email_available(email: $email)
      } 
    `,
    variables: {
      email
    }
  });
};

export const checkUserDisplayIdExist = (displayId: string) => {
  return publicClient({
    query: `
      query IsUserDisplayIdExist($display_id: String!) {
        is_user_display_id_exist(display_id: $display_id)
      }
    `,
    variables: {
      display_id: displayId
    }
  });
};

export type UpdateKycRequest = {
  userId: string;
  kycStatus: kycStatusType;
};

export const updateKYC = (data: UpdateKycRequest) => {
  return client({
    query: `
    mutation MyMutation($input: UpdateSecurityStatusInput) {
      update_security_status(input: $input) {
        security_id
      }
    }
    `,
    variables: {
      input: {
        object_id: data.userId,
        object_type: "USER",
        security_status: data.kycStatus,
        security_type: "FRANKIE"
      }
    }
  });
};

export const getCardSetting = (userId: string) => {
  return client({
    query: `query GetCardSettings($input: GetCardSettingsInput) {
      get_card_settings(input: $input) { 
        card_setting_id
	      card_setting_status
	      status
	      ppan
	      card_id
	      wallet_id
	      expires_at
 	      sub_type
	      user_id 
        updated_at
      } 
    }`,
    variables: {
      input: {
        user_id: userId
      }
    }
  });
};

export interface SearchCardSetting extends PaginationRequest {
  cardSettingFilter?: {
    card_id?: string;
    wallet_id?: string;
    status?: string;
    organisation_id?: string;
  };
}

export const searchCardSetting = (data: SearchCardSetting) => {
  return client({
    query: `query SearchCardSetting($input: SearchInput) {
      search_card_settings(input: $input) { 
        items {
          card_setting_id
	        card_setting_status
	        status
	        ppan
	        card_id
	        wallet_id
	        expires_at
 	        sub_type
	        user_id 
          updated_at
        }
	      total_pages
	      total_size
      } 
    }`,
    variables: {
      input: {
        filter: {
          card_setting_filter: data.cardSettingFilter
        },
        size: data.size,
        from: data.from
      }
    }
  });
};

export const getCurrentCardSetting = (cardId: string) => {
  return client({
    query: `
    query GetCurrentCardSetting($input: GetCurrentCardSettingInput) {
      get_current_card_setting(input: $input) {
        card_setting_id
        card_setting_status
        status
        sub_type
        expires_at
        card_id
        wallet_id
        user_id
        wallet {
          balance
          name
        }
        user {
          email
          attributes {
            first_name
            last_name
          }
        }
      }
    }
  `,
    variables: {
      input: {
        card_id: cardId
      }
    }
  });
};

export const deleteCardSetting = (cardId: string, walletId: string) => {
  return client({
    query: `
    mutation RemoveCardSetting($input: RemoveCardSettingInput) {
      remove_card_setting(input: $input) {
        code
        message
      }
    }`,
    variables: {
      input: {
        card_id: cardId,
        wallet_id: walletId,
        force: true
      }
    }
  });
};

export interface UserDownloadRequest {
  userFilter?: {
    is_active?: boolean | null;
    email?: string | null;
    full_name?: string | null;
    organisation_id?: number | string | null;
    role?: string;
  };
  columns?: string[];
  timezone: string;
  report_name?: string;
}

export const downloadUsers = (data: UserDownloadRequest) => {
  return client({
    query: `query download_users($input: DownloadReportInput!) {
      download_users(input: $input) {
        report_id
      }
    }`,
    variables: {
      input: {
        filter: {
          user_filter: data?.userFilter
        },
        payload: {
          filter: {
            user_filter: data?.userFilter ?? {}
          },
          from: 0,
          size: 10000
        },
        timezone: data?.timezone,
        columns: data?.columns,
        report_name: data?.report_name
      }
    }
  });
};
